import iconAvatar from '@/assets/images/default-avatar.png';
import AddAvatar from '@/assets/svg/ic_add_avatar.svg';
import progressBackground from '@/assets/images/bg-progress-1.png';
import bgProfileName from '@/assets/images/bg-profile-name.png';
import shield2 from '@/assets/images/shield-2.png';

import { DinRoundProFont } from '@/lib/utils';
import { Popover } from '@flyer/components';
import { BgImage } from '@flyer/components/next';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { m } from 'framer-motion';
import AvatarRanking from '@/components/AvatarRanking';
import Button from '@/components/common/Button';

const TOTAL_ATTENDANCE = 365;

export default function AvatarProfile({
  isMobile,
  avatarUrl,
  attendance,
  frameImageUrl,
  displayName,
}: {
  isMobile?: boolean;
  avatarUrl?: string | null;
  frameImageUrl?: string;
  attendance: number;
  displayName?: string;
}) {
  const router = useRouter();
  const { t } = useTranslation('common');
  // const attendance = currentLearnerProfile?.star || 0;

  const handlerEditProfile = useCallback(() => {
    void router.push('/edit-profile/info-user');
  }, [router]);

  if (isMobile) {
    return (
      <div
        className={twMerge(
          'relative flex flex-col items-center w-full ',
          DinRoundProFont.className,
        )}
      >
        <div className="w-[150px] relative z-20">
          <AvatarRanking
            avatar={avatarUrl || iconAvatar}
            frame={frameImageUrl}
            className="w-full h-full cursor-pointer"
          />
          <m.div className="absolute right-[10px] top-[40%] cursor-pointer">
            <AddAvatar className="w-[26px]" />
          </m.div>
        </div>
        <Popover content={displayName} onlyShowOnEllipsis zIndex={10_000}>
          <h3
            className={twMerge('text-base font-bold text-white relative z-20 w-full text-center')}
          >
            {displayName}
          </h3>
        </Popover>
        <div className="relative w-[90%] bg-[#26145A] rounded-[8px] mt-2">
          <div className="flex flex-row items-center justify-between py-1 px-2">
            <div className="w-full relative sm:mx-2 bg-[#2D0048] rounded-[4px] p-[1px] h-[24px] ">
              {attendance !== 0 && (
                <Button
                  colorScheme="blue-1"
                  className="h-full p-0"
                  style={{
                    width:
                      attendance > TOTAL_ATTENDANCE
                        ? '100%'
                        : `${(attendance / TOTAL_ATTENDANCE) * 100}%`,
                  }}
                  animation={false}
                />
              )}
              <span
                className={twMerge(
                  'text-white text-base absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold',
                  DinRoundProFont.className,
                )}
              >
                {attendance}/{TOTAL_ATTENDANCE}
              </span>
              <span className="text-white text-[14.4px] opacity-60 font-bold absolute top-1/2 right-0 transform -translate-x-1/2 -translate-y-1/2">
                {t('label.star')}
              </span>
            </div>
            <div className="2xl:w-[65px] w-[40px]" />
            <Image
              src={shield2}
              alt="flyer"
              className="max-w-[40px] 2xl:max-w-[56px] absolute right-0"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-row items-center ">
      <div className="w-[150px] relative z-20">
        <AvatarRanking
          avatar={avatarUrl || iconAvatar}
          frame={frameImageUrl}
          className="w-full h-full cursor-pointer"
        />
      </div>
      <div className="ml-[-50px]">
        <div
          data-testid="box-profile-name"
          className="relative h-[65px] w-[361px] flex items-center justify-between"
        >
          <Image src={bgProfileName} alt="flyer" className="absolute w-full h-full z-10" />
          <Popover content={displayName} onlyShowOnEllipsis zIndex={10_000}>
            <h3
              className={twMerge(
                'text-xl font-bold relative z-20 w-[80%] pl-[46px] text-white',
                DinRoundProFont.className,
              )}
            >
              {displayName}
            </h3>
          </Popover>
          <div
            aria-hidden
            onClick={handlerEditProfile}
            className="w-[20px] relative z-20 mr-4 cursor-pointer"
          />
        </div>
        <div data-testid="box-profile-attendance" className="max-w-[430px]">
          <BgImage
            src={progressBackground}
            alt="flyer"
            className="w-full flex flex-row items-center justify-between"
            wrapClassName="flex flex-row items-center"
            imageClassName="w-full h-[40px] xl:h-[45.6px] 2xl:h-[57.6px]"
          >
            <div className="w-[20px]" />
            <div className="w-full relative sm:mx-2 bg-[#2D0048] rounded-[4px] p-[1px] h-[24px] 2xl:h-[33px] mx-1">
              {attendance !== 0 && (
                <Button
                  colorScheme="blue-1"
                  className="h-full p-0"
                  style={{
                    width:
                      attendance > TOTAL_ATTENDANCE
                        ? '100%'
                        : `${(attendance / TOTAL_ATTENDANCE) * 100}%`,
                  }}
                  animation={false}
                />
              )}
              <span
                className={twMerge(
                  'text-white text-xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold',
                  DinRoundProFont.className,
                )}
              >
                {attendance}/{TOTAL_ATTENDANCE}
              </span>
              <span className="text-white text-[14.4px] opacity-60 font-bold absolute top-1/2 right-0 transform -translate-x-1/2 -translate-y-1/2 uppercase">
                {t('label.star')}
              </span>
            </div>
            <div className="2xl:w-[65px] w-[40px]" />
            <Image
              src={shield2}
              alt="flyer"
              className="max-w-[40px] 2xl:max-w-[56px] absolute right-0"
            />
          </BgImage>
        </div>
      </div>
    </div>
  );
}
